import React, { useEffect, useMemo, useState } from "react";
import "./TextPanel.css";

/**
 * Painel de texto reutilizável com/sem abas.
 *
 * Props:
 * - useTabs?: boolean — ativa/desativa abas.
 * - tabs?: Array<{ key?: string; label: string; html?: string; text?: string; node?: React.ReactNode }>
 * - defaultTab?: number | string — índice ou key inicial.
 * - onTabChange?: (index, tab) => void — callback.
 * - contentHtml?: string — modo sem abas (HTML).
 * - contentText?: string — modo sem abas (texto).
 * - children?: React.ReactNode — modo sem abas (conteúdo JSX).
 * - width?: string | number — define a largura do painel.
 * - height?: string | number — define a altura do painel.
 * - className?: string — estilização extra.
 * - emptyMessage?: string — texto mostrado quando não há conteúdo.
 */
export default function TextPanel({
  useTabs = false,
  tabs = [],
  defaultTab,
  onTabChange,
  contentHtml,
  contentText,
  children,
  width = "100%",
  height = "auto",
  className = "",
  emptyMessage = "Sem conteúdo.",
}) {
  const normalizedTabs = useMemo(() => {
    if (!Array.isArray(tabs)) return [];
    return tabs.map((t, i) => ({
      ...t,
      _index: i,
      _key: t.key ?? `tab-${i}`,
    }));
  }, [tabs]);

  const initialIndex = useMemo(() => {
    if (typeof defaultTab === "number") {
      return Math.min(Math.max(defaultTab, 0), normalizedTabs.length - 1);
    }
    if (typeof defaultTab === "string") {
      const idx = normalizedTabs.findIndex((t) => t._key === defaultTab);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  }, [defaultTab, normalizedTabs]);

  const [active, setActive] = useState(initialIndex);

  useEffect(() => {
    setActive(initialIndex);
  }, [initialIndex]);

  const handleTabClick = (i) => {
    setActive(i);
    if (onTabChange) onTabChange(i, normalizedTabs[i]);
  };

  const renderTabContent = (tab) => {
    if (tab?.node) return tab.node;
    if (tab?.html) return <div dangerouslySetInnerHTML={{ __html: tab.html }} />;
    if (tab?.text) return <p>{tab.text}</p>;
    return <p className="textpanel-empty">{emptyMessage}</p>;
  };

  const style = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  if (!useTabs) {
    return (
      <div className={`textpanel-container ${className}`} style={style}>
        <div className="textpanel-body">
          {contentHtml ? (
            <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
          ) : contentText ? (
            <p>{contentText}</p>
          ) : (
            children ?? <p className="textpanel-empty">{emptyMessage}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`textpanel-container ${className}`} style={style}>
      <div className="textpanel-tabs">
        {normalizedTabs.map((tab, i) => (
          <button
            key={tab._key}
            className={`textpanel-tab ${i === active ? "active" : ""}`}
            onClick={() => handleTabClick(i)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="textpanel-body">
        {normalizedTabs.length > 0
          ? renderTabContent(normalizedTabs[active])
          : <p className="textpanel-empty">{emptyMessage}</p>}
      </div>
    </div>
  );
}
